import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { CertProService } from '@services/core/certpro.service';
import { EntityService } from '@services/core/entity.service';
import { NgbModal, NgbModalRef, NgbNavModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { merge } from 'rxjs';
import { UniversalViewerComponent } from '@components/core/viewer/universal-viewer.component';
import Swal from 'sweetalert2'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '@services/core/toast.service';

@Component({
  selector: 'app-evaluation-certpro',
  templateUrl: './evaluation.component.html',
  styleUrls: ["./evaluation.component.scss"],
  imports: [CommonModule, DragDropModule, FormsModule, NgbNavModule, NgbTooltipModule, UniversalViewerComponent],
  providers: [CertProService, EntityService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class CertproEvaluationComponent implements OnInit {
  @Input() period
  @Input() entity
  @Input() review = false
  @Output() backEvent = new EventEmitter()

  protected certpro
  private categoriesTemp
  protected showEditCategory = false
  private certProService = inject(CertProService)
  private entityService = inject(EntityService)
  private toastService = inject(ToastService)
  protected items
  protected item_sel
  protected certProTypes
  protected typeCertPro
  protected blob
  private modalService = inject(NgbModal)
  private destroyRef = inject(DestroyRef)
  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: null
  }
  public closeResult = ""
  public question = ""
  activeCategory: number | null = null;
  protected tooltip
  private item = null
  protected years = []
  protected fileURL
  protected comment
  protected modalConfig
  protected filesToUpload
  protected evidenceOpen = false
  protected evidenceItem: any = null
  protected editingComment = false

  protected get isExpired(): boolean {
    if (!this.certpro?.period?.expiration) return false;
    return new Date() > new Date(this.certpro.period.expiration);
  }


  ngOnInit(): void {
    if (!this.period) {
      let today = new Date()
      this.period = today.getFullYear() + '-' + (today.getMonth() + 1 < 10 ? '0' + (today.getMonth() + 1) : (today.getMonth() + 1))
    }
    this.getPeriod()
  }

  toggleCategory(index: number): void {
    this.activeCategory = this.activeCategory === index ? null : index;
  }

  getPeriod() {
    this.certProService.getPeriod(this.period, this.entity).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.certpro = res
      if (this.certpro?.period?.realized) this.review = true
      this.updateScore()
    })
  }

  updateAnswer(item) {
    if (item.answer === true) {
      item.comments = null
    }
    if (item.answer === false) {
      item.fileId = null
      item.file = null
    }
    this.certProService.updateAnswer(item).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.updateScore()
    })
  }

  private calculate(items, rule) {
    let count = 0;
    let score = 0;

    for (const item of items) {
      const elements = item.subitems?.length ? item.subitems : [item];

      for (const el of elements) {
        count++;
        score += rule(el);
      }
    }

    return { count, score };
  }

  private calculateScore(items) {
    return this.calculate(
      items,
      el => (el.answer === true ? (el.file ? 1 : 0.5) : 0)
    );
  }

  private calculateCategoryScore(items) {
    return this.calculate(
      items,
      el => (el.answer != null ? (el.file || el.comments ? 1 : 0.5) : 0)
    );
  }

  updateScore() {
    let totalCount = 0;
    let totalScore = 0;

    this.certpro.evaluation.forEach((category, index) => {
      const { count, score } = this.calculateScore(category.answers);
      const categoryScore = this.calculateCategoryScore(category.answers)
      this.certpro.evaluation[index].category.completed = categoryScore.count
        ? categoryScore.score / categoryScore.count * 100 : 0

      category.answers.forEach((item: any) => {
        if (item.subitems?.length) {
          let itemTotal = 0
          let itemCount = 0
          for (const sub of item.subitems) {
            itemCount++
            if (sub.answer != null) itemTotal += 0.5
            if (sub.file || sub.comments) itemTotal += 0.5
          }
          item.question.completed = itemCount
            ? (itemTotal / itemCount) * 100 : 0
        }
      })

      totalCount += count;
      totalScore += score;
    });

    this.certpro.period.score = totalCount ? Math.round((totalScore / totalCount) * 100) : 0;
    this.saveScore();
  }

  saveScore() {
    this.certProService.updatePeriod(this.certpro.period.id, this.certpro.period.score).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      error: (err) => console.error('saveScore: error', err)
    })
  }

  finish() {
    Swal.fire({
      title: '¿Listo para enviar a revisión?',
      html: `
        <p style="text-align: left; font-size: 15px;">Estás por enviar este formulario de auditoría. Una vez enviado, no podrás modificarlo a menos que sea rechazado.</p>
        <div style="background-color: #e2f0d9; color: #385723; padding: 10px; border-radius: 5px; text-align: left; border: 1px solid #c5e1a5; font-size: 14px;">
          <strong>Recuerda:</strong> Tu registro pasará por un validador y un auditor final punto por punto. Evita observaciones asegurándote de que todo esté en orden.
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#4e73df',
      cancelButtonColor: '#858796',
      confirmButtonText: 'Sí, enviar información',
      cancelButtonText: 'Revisar un momento más'
    }).then((result) => {
      if (result.isConfirmed) {
        this.certProService.finishPeriod({ id: this.certpro.period.id }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.getPeriod()
        })
      }
    })
  }

  validate() {
    Swal.fire({
      width: 800,
      title: 'Atención: Cierre de Auditoría',
      html: `
        <div style="background-color: #f8d7da; color: #721c24; padding: 12px; border-radius: 5px; text-align: left; border: 1px solid #f5c6cb; font-size: 14px;">
          <strong>⚠️ Recordatorio de Responsabilidad:</strong> Como validador, tu aprobación garantiza que la información ya fue filtrada y es correcta. Cualquier anomalía detectada posteriormente afectará el indicador de este proceso.
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Sí, la información es correcta',
      showDenyButton: true,
      denyButtonText: 'Rechazar',
    }).then((result) => {
      if (result.isConfirmed) {
        this.certProService.signPeriod({ id: this.certpro.period.id, status: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.review = true
            this.getPeriod()
            Swal.fire('Validado', 'Formulario validado correctamente', 'success')
          },
          error: (err) => {
            Swal.fire('Error', err.error?.message || 'No se pudo validar el formulario', 'error')
          }
        })
      } else if (result.isDenied) {
        this.certProService.signPeriod({ id: this.certpro.period.id, status: false }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.review = false
            this.getPeriod()
            Swal.fire('Rechazado', 'El formulario se ha devuelto para corrección', 'info')
          },
          error: (err) => {
            Swal.fire('Error', err.error?.message || 'No se pudo rechazar el formulario', 'error')
          }
        })
      }
    })
  }

  signModal(modal) {
    this.open(modal)
  }

  sign(status) {
    this.certProService.signPeriod({ id: this.certpro.period.id, status }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getPeriod()
      this.modalService.dismissAll()
    })
  }

  auditModal(modal) {
    this.modalConfig = {
      step: 'audit',
      title: 'Confirmación de auditoría',
      message: '¿Estas seguro de completar la auditoría?',
      type: 'warning',
      showIcon: true,
      showActions: true,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      showRejectButton: true,
      rejectButtonText: 'No, Rectificar',
      showAcceptButton: true,
      acceptButtonText: 'Sí, Continuar'
    }
    this.open(modal)
  }

  audit(status) {
    this.certProService.auditPeriod({ id: this.certpro.period.id, status }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.getPeriod()
      this.modalService.dismissAll()
    })
  }

  confirm(event) {
    if (event === 'sign') this.sign(true)
    else if (event === 'audit') this.audit(true)
    else if (event === 'reopen') this.reopen(event)
  }

  reject(event) {
    if (event === 'sign') this.sign(false)
    if (event === 'audit') this.audit(false)
  }

  reopen(event) {
    // this.certpro.reopen = true
    // this.saveChanges()
    // this.modalService.dismissAll()
  }

  open(content, item?) {
    if(item) this.item = item
    this.modalService.open(content, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `${reason}`;
    });
  }

  modalInfo(): void {
    this.modalConfig = {
      step: 'reopen',
      title: 'Reapertura de periodo',
      message: 'Seleccione la nueva fecha de cierre',
      type: 'info',
      showDate: true
    }

  }

  viewFile(modal, item) {
    this.item = item
    if (item.fileId) {
      this.certProService.getFile(item.fileId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
        this.blob = res
        this.open(modal)
      })
    } else {
      this.fileURL = null
      this.open(modal)
    }
  }

  uploadFile(item: any, file: File) {
    if (!file) {
      this.toastService.warning('Selecciona un archivo antes de subir.')
      return;
    }

    const formData = new FormData();
    formData.append('file', file)

    this.certProService.uploadFile(item, formData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        item.fileId = res.file.id
        item.file = res.file
        this.updateAnswer(item)
        this.toastService.success('Archivo subido correctamente.')
        this.closeEvidencePanel()
      },
      error: (err) => {
        console.error('Error al subir archivo:', err);
        this.toastService.error('Error al subir el archivo. Intente nuevamente.')
      }
    });
  }

  openEvidencePanel(item: any) {
    this.evidenceItem = item
    this.evidenceOpen = true
    this.editingComment = false
  }

  closeEvidencePanel() {
    this.evidenceOpen = false
    this.evidenceItem = null
    this.editingComment = false
  }

  startEditComment() {
    this.editingComment = true
  }

  cancelEditComment() {
    this.editingComment = false
  }

  saveComment(item: any) {
    this.updateAnswer(item)
    this.toastService.success('Motivo guardado correctamente.')
    this.editingComment = false
    this.closeEvidencePanel()
  }

  downloadFile(){
    if (this.blob) {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(this.blob)
      a.download = this.item.file?.originalName || 'archivo'
      a.click()
      URL.revokeObjectURL(a.href)
    }
  }


  back() {
    this.backEvent.emit()
  }

} 